import { useEffect, useMemo, useState } from 'react'
import { Box, IconButton } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import NextImage from 'next/image'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import VariantImages from '@/assets/VariantImages'
import { useDeviceDetector } from '@/hooks'
import { gtmSendCustomEvent } from '@/helpers/GTMHelper'
import useRNWebViewTranslation from '@/hooks/useRNWebViewTranslation'
import { usePetsittingMode } from '@/store/usePetsittingMode'
import { EmtTypography } from 'emt-components-v2'

type Props = {
  enabled?: boolean
  dismissDays?: number
  excludedPathPrefixes?: string[]
}

const DISMISS_KEY = 'emt:web:app-install-banner:dismissed-until'

// Safari affiche déjà la Smart App Banner native (meta apple-itunes-app dans _document.tsx)
const isIOSSafari = (ua: string) =>
  /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS|OPT\/|GSA\/|YaBrowser|Ddg\//i.test(ua)

// Les WebViews (navigateurs in-app Facebook, Instagram...) et Firefox ne gèrent pas tous les URLs intent://
const supportsAndroidIntent = (ua: string) => !/; wv\)/i.test(ua) && /Chrome\/|SamsungBrowser\//i.test(ua)

// Chrome ouvre l'app si elle est installée et gère cette URL, sinon il navigue vers le store (browser_fallback_url)
const buildAndroidIntentUrl = (appUrl: string, packageName: string, fallbackUrl: string) => {
  const normalizedUrl = appUrl.replace(/^https?:\/\//, '')
  const encodedFallback = encodeURIComponent(fallbackUrl)

  return `intent://${normalizedUrl}#Intent;scheme=https;package=${packageName};S.browser_fallback_url=${encodedFallback};end`
}

const AppInstallBanner = ({ enabled = true, dismissDays = 7, excludedPathPrefixes = ['/ghost'] }: Props) => {
  const router = useRouter()
  const { t } = useTranslation('common')
  const { device } = useDeviceDetector()
  const hasRNWebView = useRNWebViewTranslation()
  const { isPetsittingMode } = usePetsittingMode()
  const [userAgent, setUserAgent] = useState<string | null>(null)
  const [isDismissed, setIsDismissed] = useState(false)

  useEffect(() => {
    setUserAgent(navigator.userAgent)

    try {
      const rawValue = localStorage.getItem(DISMISS_KEY)
      const dismissedUntil = rawValue ? Number(rawValue) : 0
      setIsDismissed(Number.isFinite(dismissedUntil) && dismissedUntil > Date.now())
    } catch (_error) {
      // localStorage indisponible (navigation privée, WebView restreinte) : on affiche le bandeau
    }
  }, [])

  const links = useMemo(() => {
    if (isPetsittingMode) {
      return {
        store: {
          ios: 'https://apps.apple.com/fr/app/super-sitters/id6759970836',
          android: 'https://play.google.com/store/apps/details?id=app.supersitters.app',
        },
        appUrl: 'https://app.supersitters.app',
        androidPackage: 'app.supersitters.app',
      }
    }

    return {
      store: {
        ios: 'https://apps.apple.com/fr/app/id1403471929',
        android: 'https://play.google.com/store/apps/details?id=com.empruntemontoutou.app',
      },
      appUrl: 'https://app.empruntemontoutou.com',
      androidPackage: 'com.empruntemontoutou.app',
    }
  }, [isPetsittingMode])

  const content = useMemo(() => {
    if (isPetsittingMode) {
      return {
        appName: t('appInstallBanner.appNameSST'),
        subtitle: t('appInstallBanner.subtitle'),
        appIcon: VariantImages.SSSelect,
      }
    }

    return {
      appName: t('appInstallBanner.appName'),
      subtitle: t('appInstallBanner.subtitle'),
      appIcon: VariantImages.EmtSelect,
    }
  }, [isPetsittingMode, t])

  const shouldHideForRoute = excludedPathPrefixes.some((prefix) => router.pathname.startsWith(prefix))
  const isMobileDevice = device === 'iOS' || device === 'Android'

  if (
    !enabled ||
    userAgent === null ||
    hasRNWebView ||
    shouldHideForRoute ||
    isDismissed ||
    !isMobileDevice ||
    (device === 'iOS' && isIOSSafari(userAgent))
  ) {
    return null
  }

  // Lien natif (pas de window.location ni de target="_blank") : seul un vrai tap utilisateur
  // sur un <a> garantit l'ouverture de l'App Store / du Play Store depuis tous les navigateurs.
  const ctaHref =
    device === 'iOS'
      ? links.store.ios
      : supportsAndroidIntent(userAgent)
      ? buildAndroidIntentUrl(links.appUrl, links.androidPackage, links.store.android)
      : links.store.android

  const onDismiss = () => {
    try {
      const dismissedUntil = Date.now() + dismissDays * 24 * 60 * 60 * 1000
      localStorage.setItem(DISMISS_KEY, String(dismissedUntil))
    } catch (_error) {
      // localStorage indisponible : le bandeau est masqué pour la session en cours uniquement
    }
    setIsDismissed(true)
  }

  const onOpenStoreOrApp = () => {
    gtmSendCustomEvent('app_install_banner_click_open', {
      pageUrl: router.asPath,
      device,
      appMode: isPetsittingMode ? 'petsitting' : 'emt',
    })
  }

  return (
    <Box
      sx={{
        width: '100%',
        top: 0,
        zIndex: 1500,
        backgroundColor: '#FFFFFF',
        border: '1px solid #E6E9EF',
        boxShadow: '0 10px 26px rgba(15, 23, 42, 0.16)',
        p: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: '14px',
          overflow: 'hidden',
          flexShrink: 0,
          position: 'relative',
        }}
      >
        <NextImage src={content.appIcon} alt={content.appName} fill style={{ objectFit: 'cover' }} />
      </Box>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <EmtTypography variant="body1" fontWeight={800} lineHeight="1">
          {content.appName}
        </EmtTypography>
        <EmtTypography variant="caption" color="textSecondary" lineHeight="1">
          {content.subtitle}
        </EmtTypography>
      </Box>

      <a
        href={ctaHref}
        title="open-store"
        onClick={onOpenStoreOrApp}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '9px 18px',
          borderRadius: '999px',
          background: '#0A7CFF',
          color: '#FFFFFF',
          fontWeight: 800,
          fontSize: '16px',
          textDecoration: 'none',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        Ouvrir
      </a>

      <IconButton
        size="small"
        onClick={onDismiss}
        sx={{ color: '#6B7280', padding: '2px', marginRight: '-4px', flexShrink: 0 }}
        aria-label="Fermer le bandeau"
      >
        <CloseIcon sx={{ fontSize: 24 }} />
      </IconButton>
    </Box>
  )
}

export default AppInstallBanner
